import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TableRow,
  TableCell,
} from '@cargomatic/ui';
import { sluggify } from 'utils/localization/utils';
import { getFormatDateTimeWithRegionalSettings } from 'common/helpers/dateUtils';
import { getFileIconClass } from '../../../helpers/helper_functions';
import { DocumentDeleteButton } from './DocumentDeleteButton';
import Badge from '../../v2/Badge';
import { getSignedUrl } from '../../../utils/DocumentsAPIUtils';
import { showToast } from '../../../stores/ToastStore';

export const DOCUMENT_STATUSES = {
  PENDING: 'PENDING',
  UPLOADED: 'UPLOADED',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
};

export const DOCUMENT_STATUSES_TITLE_MAP = {
  [DOCUMENT_STATUSES.PENDING]: 'Pending',
  [DOCUMENT_STATUSES.UPLOADED]: 'Uploaded',
  [DOCUMENT_STATUSES.UPLOAD_FAILED]: 'Upload failed',
};

export const Document = ({
  isOrder,
  document,
  deleteDisabled,
  onDeleteConfirm,
  onDeleteDecline,
  isSent,
}) => {
  const [url, setUrl] = useState(null);
  const { t } = useTranslation();
  const documentName = t(sluggify(document.name));
  const extension = (
    (document?.key || '').split('.').pop() || ''
  ).toUpperCase();

  useEffect(() => {
    setUrl(null);
  }, [document.key]);

  const openDocument = async () => {
    let signedUrl = url;

    if (!url) {
      if (!document?.key) {
        showToast(
          t('GET_SIGNED_URL_ERROR'),
          t('DOCUMENT_URI_MISSING', { docName: document?.name || 'DOCUMENT' }),
          'error',
        );
        return;
      }

      const { data, errors } = await getSignedUrl(document.key);

      if (errors) {
        showToast(
          t('GET_SIGNED_URL_ERROR'),
          t('GET_SIGNED_URL_FAILED_WITH_ERROR', { error: errors[0].message }),
          'error',
        );

        return;
      }

      signedUrl = data?.getSignedUrl?.signedUrl;
      if (!signedUrl) {
        showToast(
          t('GET_SIGNED_URL_ERROR'),
          t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
            error: 'Missing signed URL',
          }),
          'error',
        );
        return;
      }

      setUrl(signedUrl);
    }

    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  };

  return (
    <TooltipProvider>
      <TableRow data-testid="tr-5721e957">
        <TableCell className="whitespace-nowrap" data-testid="td-f85c0370">
          <div className="flex items-center space-x-2">
            <i className={getFileIconClass(extension)} />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="a-f71a9fae"
                  type="button"
                  className="block max-w-[200px] truncate text-left text-blue-600 hover:underline"
                  onClick={openDocument}
                >
                  {documentName}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-md break-all">
                {documentName}
              </TooltipContent>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="td-150be71d">
          {document.createdAt &&
            getFormatDateTimeWithRegionalSettings(document.createdAt)}
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="td-e00aae74">
          <div className="flex items-center space-x-1">
            {isSent && (
              <Badge dataTestId="Badge-3e5eecae" color="green">
                {t('EMAIL_SENT')}
              </Badge>
            )}
            {document.signatory && !isSent && (
              <Badge dataTestId="Badge-47376021" color="green">
                {document.signatory}
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="td-7d40b57e">
          {document.status && (
            <span className="text-sm text-muted-foreground">
              {t(
                sluggify(
                  DOCUMENT_STATUSES_TITLE_MAP[document.status] || 'Unknown',
                ),
              )}
            </span>
          )}
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="td-99e8b169">
          {document.firstName} {document.lastName}
        </TableCell>
        <TableCell className="whitespace-nowrap" data-testid="td-661bca7f">
          {!deleteDisabled && (
            <DocumentDeleteButton
              isOrder={isOrder}
              document={document}
              onConfirm={onDeleteConfirm}
              onDecline={onDeleteDecline}
            />
          )}
        </TableCell>
      </TableRow>
    </TooltipProvider>
  );
};
